import React from 'react';
import type { CollapseProps } from 'antd';
import dayjs, { Dayjs } from 'dayjs';
import { Collapse, ConfigProvider } from 'antd';
import { Form, useFormikContext } from 'formik';
import {
  AphDatePicker,
  AphSelect,
  IUsePaginatedService,
  useFetchServiceV3,
} from '@aph/common-react';
import { defaultPageSize } from 'global.constants';
import { updateUrlSearchParameters } from 'global.helpers';
import { setupFilteredTagsList } from 'helpers/tag.helpers';
import {
  handleRemoveSelectedSearchFilterTag,
  setupDateForRequest
} from 'helpers/form.helpers';
import {
  ILegislationSearchFormData
} from 'types/form.types';
import {
  IBillsDigestSearchRequest,
  IBillsDigestSearchResponse,
  IGetLookupResponse,
  IGetParliamentsResponse,
  IUseSearchService
} from 'services';

import {
  NoResultsFound,
  SearchBar,
  SearchFilteredTags,
  SearchResultItem,
  SearchResultsWrapper
} from 'components';

import './LegislationSearchInner.scss';

/**
 * IPublicationSearchInner
 *
 * @description Component properties for the publication search component
 * @property {IUseSearchService}                                                             searchService        Service for searching
 * @property {IUsePaginatedService<IBillsDigestSearchRequest> & IBillsDigestSearchResponse}  searchData           Search data
 */
interface ILegislationSearchInner {
  searchService: IUseSearchService;
  searchData: IUsePaginatedService<IBillsDigestSearchRequest> & IBillsDigestSearchResponse;
  searchRefreshHandler: (request: IBillsDigestSearchRequest) => void;
}

/**
 * LegislationSearchInner
 *
 * @description Inner search component for handling the search filters for legislation search
 */
const LegislationSearchInner: React.FC<ILegislationSearchInner> = ({ searchService, searchData, searchRefreshHandler }) => {
  const { items: portfolios } = useFetchServiceV3<undefined, IGetLookupResponse>(searchService.getSearchPortfolios, { loadOnInitiate: true });
  const { parliaments } = useFetchServiceV3<undefined, IGetParliamentsResponse>(searchService.getParliaments, { loadOnInitiate: true });

  const { values: formValues, setFieldValue } = useFormikContext<ILegislationSearchFormData>();

  /**
   * HandleFormFieldOnChange
   *
   * @description On change handler for filters
   * @param {string}                 key   Form field name
   * @param {string | Array<string>} value Field value
   * @returns {void}
   */
  const handleFormFieldOnChange = (key: string, value: string | Array<string>): void => {
    updateUrlSearchParameters(key, value);

    const updatedSearchRequest: IBillsDigestSearchRequest = {
      ...searchData.request,
      currentPage: 1,
      [key]: value
    };

    searchRefreshHandler(updatedSearchRequest);
  };

  /**
   * HandleDateFromFieldOnChange
   *
   * @description On change handler for the date field
   * @param {Dayjs | null} value Selected date values
   * @returns {void}
   */
  const handleDateFromFieldOnChange = (value: Dayjs | null | null): void => {
    const updatedUrlParameter = value ? value.format('YYYY-MM-DD') : null;

    updateUrlSearchParameters('dateFrom', updatedUrlParameter);

    const dataRequestSetup = setupDateForRequest(value);
    const updatedSearchRequest: IBillsDigestSearchRequest = {
      ...searchData.request,
      currentPage: 1,
      dateFrom: dataRequestSetup,
    };

    searchRefreshHandler(updatedSearchRequest);
  };

  /**
   * HandleDateToFieldOnChange
   *
   * @description On change handler for the date field
   * @param {Dayjs | null} value Selected date values
   * @returns {void}
   */
  const handleDateToFieldOnChange = (value: Dayjs | null | null): void => {
    const updatedUrlParameter = value ? value.format('YYYY-MM-DD') : null;

    updateUrlSearchParameters('dateTo', updatedUrlParameter);

    const dataRequestSetup = setupDateForRequest(value);
    const updatedSearchRequest: IBillsDigestSearchRequest = {
      ...searchData.request,
      currentPage: 1,
      dateTo: dataRequestSetup,
    };

    searchRefreshHandler(updatedSearchRequest);
  };

  /**
   * HandleParliamentSelectChange
   *
   * @description Event handler for selecting a parliament
   * @param {string} selectedValue Selected parliament number
   * @returns {void}
   */
  const handleParliamentSelectChange = (selectedValue: string): void => {
    // The reason we make both the numbers strings is ANTD types their select values as strings but ours is actually a number
    // So to avoid compiler typescript errors we just make them both strings to ensure there is no weirdness
    const selectedParliament = parliaments.find(parliament => `${parliament.number}` === `${selectedValue}`);

    const updatedDateFrom = selectedParliament && selectedParliament.startDate ? dayjs(selectedParliament.startDate) : null;
    const updatedDateTo = selectedParliament && selectedParliament.endDate ? dayjs(selectedParliament.endDate) : null;

    setFieldValue('dateFrom', updatedDateFrom);
    setFieldValue('dateTo', updatedDateTo);

    handleDateFromFieldOnChange(updatedDateFrom);
    handleDateToFieldOnChange(updatedDateTo);
  };

  // This defines the content for the collapse
  const items: CollapseProps['items'] = [
    {
      key: '1',
      label: 'Filters',
      children:
        <div className='library-publication-search__filters-inner'>
          <div className='library-publication-search__filter-column'>
            <h3>Portfolio</h3>
            <AphSelect
              name='portfolios'
              mode='multiple'
              placeholder='Select Portfolio(s)...'
              options={portfolios ?? []}
              allowClear
              onChange={(value: string | Array<string>) => handleFormFieldOnChange('portfolios', value)}
            />
          </div>

          <div className='library-publication-search__filter-column'>
            <h3>Period</h3>
            <AphSelect
              name='period'
              placeholder='Select parliament'
              allowClear
              onChange={(value: string | Array<string>) => handleParliamentSelectChange(value as string)}
              options={parliaments
                ? parliaments.map(parliament => ({ value: parliament.number, label: `${parliament.number}` }))
                : []
              }
            />
          </div>

          <div className='library-publication-search__filter-column'>
            <h3>When</h3>
            <div className='library-publication-search__filter-date'>
              <AphDatePicker
                name='dateFrom'
                placeholder='Start date'
                onChange={(value: Dayjs | null) => handleDateFromFieldOnChange(value)}
              />
              <AphDatePicker
                name='dateTo'
                placeholder='End date'
                onChange={(value: Dayjs | null) => handleDateToFieldOnChange(value)}
              />
            </div>
          </div>
        </div>
    }
  ];

  return (
    <Form className='legislation-form'>
      <div className='library-publication-search__filters'>
        <SearchBar
          theme='legislation'
          name='searchTerms'
          showClear
        />
        <ConfigProvider
          theme={{
            token: {
              borderRadiusLG: 0,
              lineWidth: 0
            }
          }}
        >
          <Collapse
            className='library-publication-search__filters--additional'
            defaultActiveKey={['1']}
            items={items}
          />
        </ConfigProvider>
      </div>
      <SearchResultsWrapper
        loading={searchData.loading && (!searchData.results || searchData.results.length <= 0)}
        paginationConfig={{
          currentPage: searchData.currentPage ?? 1,
          totalResults: searchData.rowCount ?? 0,
          pageSize: searchData.pageSize ?? defaultPageSize,
          onPaginate: searchData.updatePagination
        }}
      >
        <SearchFilteredTags
          tags={setupFilteredTagsList('portfolios', formValues.portfolios, portfolios)}
          onRemove={(id, fieldName) =>
            handleRemoveSelectedSearchFilterTag(fieldName, id, formValues.portfolios, setFieldValue, handleFormFieldOnChange)
          }
        />
        {
          searchData.results &&
          searchData.results.length > 0
            ?
            <>
              {
                searchData.results.map((searchResult, index) =>
                  <SearchResultItem
                    key={index}
                    result={{
                      ...searchResult,
                      type: searchResult.type,
                      categories: searchResult.tags
                    }}
                  />
                )
              }
            </>
            : <NoResultsFound />
        }
      </SearchResultsWrapper>
    </Form>
  );
};

export default LegislationSearchInner;
